/**
 * WorkerPool RSS-cap monitor.
 *
 * @module worker-pool/rss-monitor
 */

import { readFile } from 'node:fs/promises';
import type { BrowserLogger } from '../logger.js';
import type { PooledChild } from './bookkeeping.js';
import { WorkerPoolOomError } from './errors.js';

/**
 * Per-process RSS from `/proc/<pid>/status` (`VmRSS:`, reported in **kB** — so it
 * is page-size independent, unlike `statm`'s page count which would undercount
 * 4–16× on a large-page ARM64 kernel). A single short read with no VMA
 * aggregation — cheaper than the `smaps_rollup` the accurate 60 s telemetry path
 * uses, which is the right trade for a coarse RSS backstop. Returns `undefined`
 * off-Linux or on any read/parse error, which the monitor treats as "no
 * enforcement" — the safe fallback for macOS dev/test.
 */
export async function readProcRssBytes(pid: number): Promise<number | undefined> {
  let text: string;
  try {
    text = await readFile(`/proc/${pid}/status`, 'utf8');
  } catch {
    return undefined;
  }
  return parseVmRssBytes(text);
}

/** Parse the `VmRSS:` line of a `/proc/<pid>/status` payload (kB → bytes). */
export function parseVmRssBytes(text: string): number | undefined {
  const match = text.match(/^VmRSS:\s+(\d+)\s+kB/m);
  return match ? Number(match[1]) * 1024 : undefined;
}

/**
 * Per-process PSS from `/proc/<pid>/smaps_rollup` (`Pss:`, in kB). PSS divides
 * each shared page by its sharer count, so summing it across a process *group*
 * measures the group's true incremental footprint — summing `VmRSS` instead
 * counts Chromium's shared text/zygote pages once per process (~3× inflation
 * across a browser tree). Costlier than the `status` read (the kernel walks
 * VMAs), so callers should reserve it for small pid sets. Returns `undefined`
 * off-Linux or on any read/parse error.
 */
export async function readProcPssBytes(pid: number): Promise<number | undefined> {
  let text: string;
  try {
    text = await readFile(`/proc/${pid}/smaps_rollup`, 'utf8');
  } catch {
    return undefined;
  }
  return parsePssBytes(text);
}

/**
 * Parse the `Pss:` line of a `/proc/<pid>/smaps_rollup` payload (kB → bytes).
 * Anchored so the `Pss_Anon:`/`Pss_File:`/`Pss_Shmem:` sibling lines never match
 * — a mis-parse here would silently fall back to VmRSS and quietly reintroduce
 * the ~3× shared-page inflation this reader exists to avoid.
 */
export function parsePssBytes(text: string): number | undefined {
  const match = text.match(/^Pss:\s+(\d+)\s+kB/m);
  return match ? Number(match[1]) * 1024 : undefined;
}

/** How long to wait for a freshly spawned child to become ready. */
export const DEFAULT_READY_TIMEOUT_MS = 10_000;

/**
 * Consecutive monitor passes where the cap is armed and children are present but
 * *every* RSS read fails, after which we warn once. This catches a silent
 * false-green — the monitor running while enforcement is effectively off (e.g. a
 * `/proc` mount that blocks the read) — without spamming the expected off-Linux
 * case, which is gated on the platform check at the warn site.
 */
const RSS_READ_FAILURE_WARN_STREAK = 5;

/**
 * RSS-cap circuit-breaker. An OOM kill within this window of a child becoming
 * *ready* means the cap sits below the child's baseline RSS. After enough
 * consecutive immediate kills, disable the monitor so respawned children survive
 * and the pool serves uncapped instead of looping spawn -> SIGKILL -> respawn.
 */
const IMMEDIATE_OOM_WINDOW_MS = 5_000;
const IMMEDIATE_OOM_BREAKER_THRESHOLD = 5;

export type WorkerPoolRssMonitorStats = {
  rssHighWaterBytes: number;
  rssBreakerTripped: boolean;
};

export type WorkerPoolRssMonitorOptions<Handle> = {
  children: Set<PooledChild<Handle>>;
  rssBytes: number | undefined;
  readRssBytes?: (pid: number) => Promise<number | undefined>;
  rssCheckIntervalMs?: number;
  logger: BrowserLogger;
  now: () => number;
  isDraining: () => boolean;
  onOom: (worker: PooledChild<Handle>, error: WorkerPoolOomError) => void;
  getTotalOomKills: () => number;
};

export class WorkerPoolRssMonitor<Handle> {
  private readonly children: Set<PooledChild<Handle>>;
  private readonly rssBytes: number | undefined;
  private readonly readRssBytes: (pid: number) => Promise<number | undefined>;
  private readonly rssCheckIntervalMs: number;
  private readonly logger: BrowserLogger;
  private readonly now: () => number;
  private readonly isDraining: () => boolean;
  private readonly onOom: (worker: PooledChild<Handle>, error: WorkerPoolOomError) => void;
  private readonly getTotalOomKills: () => number;

  private rssTimer: ReturnType<typeof setInterval> | null = null;
  /** Skip a tick if the previous async pass is still reading. */
  private rssSampling = false;
  private rssHighWaterBytes = 0;
  /** Consecutive passes where every child's RSS read failed (false-green guard). */
  private rssReadFailureStreak = 0;
  /** Consecutive OOM kills landing within the immediate window of readiness. */
  private consecutiveImmediateOomKills = 0;
  /** Latches true once the circuit-breaker disables the cap; never re-arms for this pool's life. */
  private rssBreakerTripped = false;

  constructor(options: WorkerPoolRssMonitorOptions<Handle>) {
    this.children = options.children;
    this.rssBytes = options.rssBytes;
    this.readRssBytes = options.readRssBytes ?? readProcRssBytes;
    this.rssCheckIntervalMs = options.rssCheckIntervalMs ?? 1000;
    this.logger = options.logger;
    this.now = options.now;
    this.isDraining = options.isDraining;
    this.onOom = options.onOom;
    this.getTotalOomKills = options.getTotalOomKills;
  }

  stats(): WorkerPoolRssMonitorStats {
    return {
      rssHighWaterBytes: this.rssHighWaterBytes,
      rssBreakerTripped: this.rssBreakerTripped,
    };
  }

  /**
   * Start the RSS monitor on first spawn, but only when a cap is configured.
   * The interval is `.unref()`'d (never holds the process open) and its tick is a
   * no-op when the pool is idle, so an armed-but-quiet pool costs ~nothing.
   */
  start(): void {
    if (this.rssBytes === undefined || this.rssTimer !== null || this.isDraining()) return;
    if (this.rssBreakerTripped) return;
    this.rssTimer = setInterval(() => void this.sampleRss(), this.rssCheckIntervalMs);
    this.rssTimer.unref?.();
  }

  stop(): void {
    if (this.rssTimer) {
      clearInterval(this.rssTimer);
      this.rssTimer = null;
    }
  }

  /**
   * Circuit-breaker bookkeeping for an OOM retire. An OOM kill within the
   * immediate window of the child becoming ready means the cap is under the
   * child's baseline — count it; once enough land in a row, trip the breaker. A
   * later (non-immediate) OOM proves the cap is workable and resets the streak.
   */
  trackImmediateOom(worker: PooledChild<Handle>): void {
    if (this.rssBreakerTripped) return;
    const lifetimeMs = this.now() - worker.readiedAt;
    if (lifetimeMs > IMMEDIATE_OOM_WINDOW_MS) {
      this.consecutiveImmediateOomKills = 0;
      return;
    }
    this.consecutiveImmediateOomKills++;
    if (this.consecutiveImmediateOomKills >= IMMEDIATE_OOM_BREAKER_THRESHOLD) {
      this.tripRssBreaker();
    }
  }

  /**
   * One monitor pass: read each readied child's RSS and SIGKILL any over the cap.
   * Guarded so passes never overlap; an idle pool's tick is a cheap no-op.
   */
  private async sampleRss(): Promise<void> {
    if (this.rssSampling || this.isDraining() || this.rssBytes === undefined) return;
    if (this.rssBreakerTripped) return;
    if (this.children.size === 0) return;
    this.rssSampling = true;
    let attempted = 0;
    let read = 0;
    try {
      // Snapshot: retiring a child can delete and respawn into this.children mid-pass.
      // eslint-disable-next-line unicorn/no-useless-spread
      for (const worker of [...this.children]) {
        if (worker.retired || worker.handle === undefined) continue;
        const pid = worker.child.pid;
        if (pid === undefined) continue;
        attempted++;
        const rss = await this.readRssBytes(pid).catch(() => undefined);
        if (rss === undefined) continue;
        read++;
        if (rss > this.rssHighWaterBytes) this.rssHighWaterBytes = rss;
        if (rss > this.rssBytes) {
          this.onOom(worker, new WorkerPoolOomError(rss, this.rssBytes));
          if (this.rssBreakerTripped) break;
        }
      }
    } finally {
      this.rssSampling = false;
    }
    this.trackRssReadHealth(attempted, read);
  }

  /**
   * Warn once if the cap is armed and children are present but every RSS read
   * keeps failing — enforcement is silently off (a false-green). Gated on Linux
   * so the expected off-platform case stays quiet.
   */
  private trackRssReadHealth(attempted: number, read: number): void {
    if (attempted === 0 || read > 0) {
      this.rssReadFailureStreak = 0;
      return;
    }
    this.rssReadFailureStreak++;
    if (
      process.platform === 'linux' &&
      this.rssReadFailureStreak === RSS_READ_FAILURE_WARN_STREAK
    ) {
      this.logger.warn(
        '[WorkerPool] RSS monitor armed but every child read failed — cap NOT enforced',
        { consecutivePasses: this.rssReadFailureStreak }
      );
    }
  }

  /**
   * Disable the RSS cap after a run of immediate OOM kills: stop the monitor so
   * respawned children survive and the pool keeps serving uncapped.
   */
  private tripRssBreaker(): void {
    if (this.rssBreakerTripped) return;
    this.rssBreakerTripped = true;
    this.stop();
    this.logger.error(
      '[WorkerPool] RSS-cap circuit-breaker TRIPPED — disabling the cap to stop a spawn/SIGKILL storm',
      {
        consecutiveImmediateKills: this.consecutiveImmediateOomKills,
        totalOomKills: this.getTotalOomKills(),
        rssCapBytes: this.rssBytes,
        immediateWindowMs: IMMEDIATE_OOM_WINDOW_MS,
      }
    );
  }
}
